import React, { useContext, useState, useEffect } from 'react';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import ServicesForm from './ServicesForm';
import ScheduleForm from './ScheduleForm';
import Address from './Preview/Address';
import Vehicle from './Preview/Vehicle';
import Services from './Preview/Services';
import Schedule from './Preview/Schedule';
import Contact from './Preview/Contact';
import NewContact from './Customer/NewContact';
import AddressForm from './AddressForm';
import VehicleForm from './VehicleForm';

import { QuickAppointmentContext } from '../Context';
import useStyles from './styles';
import { areTimeSlotsEnabled, getZ3PConfigValue } from '../helper';
import { getVehicleType } from '../VehicleConstants';

const QuickAppointmentStepper = () => {
  const classes = useStyles();
  const theme = useTheme();

  const {
    customerType,
    activeStep,
    setActiveStep,
    allSteps,
    serviceData,
    scheduleData,
    contactData,
    addressData,
    savedData,
    existingData,
    setScheduleData,
    completedSteps,
  } = useContext(QuickAppointmentContext);

  const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');

  const { first_name, email, phone } = contactData || {};
  const requiredContactDataIsFilled = !!first_name && !!email && !!phone;
  const { customer_id, customer_vehicle_id } = savedData || {};

  const [addressList, setAddressList] = useState([]);
  const [vehicleList, setVehicleList] = useState([]);

  useEffect(() => {
    if (customerType === 'existing' && existingData) {
      const { addresses } = existingData;
      if (addresses.length > 0) {
        const new_address_list = addresses.map((item) => {
          return { id: item.id, name: item.one_line_human_readable_address };
        });
        setAddressList(new_address_list);
      }

      const { vehicles } = existingData;

      if (vehicles.length > 0) {
        const existing_vehicles_list = vehicles.map((v) => {
          return {
            ...v,
            type: getVehicleType(v),
          };
        });
        setVehicleList(existing_vehicles_list);
      }
    }
  }, [existingData, setScheduleData, customerType]);

  const isContactClickable =
    customerType === 'existing' ||
    (customerType === 'new' && !customer_id && contactData);

  const isAddressClickable =
    (customerType === 'existing' && addressData) ||
    (customerType === 'new' && !addressData && requiredContactDataIsFilled);

  const isVehicleClickable =
    (customerType === 'existing' && addressData) ||
    (customerType === 'new' && addressData && !customer_vehicle_id);

  const contactHandleClick = () => {
    if (isContactClickable) setActiveStep(0);
  };

  const addressHandleClick = () => {
    if (isAddressClickable) setActiveStep(1);
  };

  const vehicleHandleClick = () => {
    if (isVehicleClickable) setActiveStep(2);
  };

  const isXSmall = useMediaQuery(theme.breakpoints.down('xs'));
  const isUsingTimeSlots = areTimeSlotsEnabled();
  const marginAndPadding = isUsingTimeSlots && isXSmall ? 0 : 'auto';
  const servicesStepIdx = allSteps.indexOf('Services');
  const scheduleStepIdx = allSteps.indexOf('Schedule');

  return (
    <div className={classes.width100}>
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step key="Contact" completed={!!contactData}>
          <StepLabel
            onClick={contactHandleClick}
            className={isContactClickable && classes.clickable}
          >
            Contact
          </StepLabel>
          <StepContent
            TransitionProps={contactData ? { in: true } : { appear: true }}
          >
            {contactData && activeStep !== 0 ? (
              <Contact />
            ) : (
              <NewContact editable={customerType === 'new'} />
            )}
          </StepContent>
        </Step>
        <Step key="Address">
          <StepLabel
            onClick={addressHandleClick}
            className={isAddressClickable && classes.clickable}
            completed={!!addressData}
          >
            Address
          </StepLabel>
          <StepContent
            TransitionProps={addressData ? { in: true } : { appear: true }}
          >
            {addressData && activeStep !== 1 ? (
              <Address />
            ) : (
              <AddressForm addressList={addressList} />
            )}
          </StepContent>
        </Step>
        {!isSkippingVehicle && (
          <Step key="Vehicle">
            <StepLabel
              onClick={vehicleHandleClick}
              className={isVehicleClickable && classes.clickable}
              completed={completedSteps[2]}
            >
              Vehicle
            </StepLabel>
            <StepContent
              TransitionProps={activeStep > 2 ? { in: true } : { appear: true }}
            >
              {activeStep !== 2 ? (
                <Vehicle />
              ) : (
                <VehicleForm vehicleList={vehicleList} />
              )}
            </StepContent>
          </Step>
        )}
        <Step key="Services">
          <StepLabel
            onClick={() => {
              if (serviceData) setActiveStep(servicesStepIdx);
            }}
            className={serviceData ? classes.clickable : ''}
          >
            Services
          </StepLabel>
          <StepContent
            TransitionProps={serviceData ? { in: true } : { appear: true }}
          >
            {serviceData && activeStep !== servicesStepIdx ? (
              <Services />
            ) : (
              <ServicesForm />
            )}
          </StepContent>
        </Step>
        <Step key="Schedule">
          <StepLabel
            onClick={() => {
              if (scheduleData) setActiveStep(scheduleStepIdx);
            }}
            className={scheduleData ? classes.clickable : ''}
          >
            Schedule
          </StepLabel>
          <StepContent
            TransitionProps={scheduleData ? { in: true } : { appear: true }}
            style={{
              marginLeft: marginAndPadding,
              paddingLeft: marginAndPadding,
              paddingRight: marginAndPadding,
            }}
          >
            {activeStep === scheduleStepIdx && <ScheduleForm />}
            {scheduleData && activeStep !== scheduleStepIdx && <Schedule />}
          </StepContent>
        </Step>
      </Stepper>
    </div>
  );
};

export default QuickAppointmentStepper;
